<template>
  <div class="company-map ml-4 mr-4">
    <div v-if="type === 'create'">
      <div class="row py-3 align-items-center">
        <div class="col company-h1">
          <button type="button"
                  class="btn btn-back"
                  @click="setCompanyStep('companies-info')"
          />
          Create Company
        </div>
        <div class="col text-right"
             v-if="$_can('companies.coverage.store')"
        >
          <button type="button"
                  class="btn btn-primary ld-ext-right"
                  :class="loadingButton ? 'running' : ''"
                  :disabled="buttonDisabled"
                  @click="saveZips(zips)"
          >
            Save
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
      <companies-steps-menu type="create" />
    </div>
    <div v-if="type === 'edit'">
      <div class="row py-3 align-items-center company-header">
        <div class="col-4">
          <h2 class="company-h1 mr-3">
            <button type="button"
                    class="btn btn-back"
                    @click="$router.push('/companies')"
            />
            {{ company.name }}
          </h2>
          <template v-if="company.status">
            <span v-if="company.status.name === 'Active'"
                  :class="'btn-status btn-active'"
            >{{ company.status.name }}</span>
            <span v-if="company.status.name === 'Inactive'"
                  :class="'btn-status btn-closed'"
            >{{ company.status.name }}</span>
            <span v-if="company.status.name === 'Setup Incomplete'"
                  :class="'btn-status btn-ncover info-company'"
            >{{ company.status.name }}
              <div class="btn btn-link p-0 tippy-popup tippy-info" @mouseover="showTooltipPrefix = true"
                   @mouseout="showTooltipPrefix = false"
              >
                <i class="fa fa-info-circle" />
              </div>
              <div class="popover tippy-info-popover" v-if="showTooltipPrefix">
                Setup incomplete is present when the company doesn't have one or more of the following: move sizes, arrival windows, merchant account, rates and coverage.
              </div>
            </span>
          </template>
        </div>
        <div class="col-8 d-flex align-items-center justify-content-end">
          <companies-visibility-bar
            :company="company"
            @saved="$emit('saved')"
            v-if="$_can('companies.review.request')"
          />
          <button type="button"
                  class="btn btn-primary ld-ext-right ml-5"
                  :class="loadingButton ? 'running' : ''"
                  :disabled="buttonDisabled"
                  @click="saveZips(zips)"
          >
            Save
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-6">
        <h6>Select the tool and draw the area, submit with Enter</h6>
        <div class="map-loader" v-if="loading">
          <clip-loader :color="loaderColor" class="clip-loader" />
        </div>
        <div id="mapid" class="map" />
      </div>
      <div class="col-6">
        <div class="select-wrap mb-3">
          <label>Search</label>
          <input type="text"
                 v-model="searchZips"
                 class="form-control mb-2"
                 placeholder="Find zip code"
          />
        </div>
        <button @click="$_can('companies.coverage.destroy') && removeZip(zip)"
                class="btn btn-dark mr-2 mb-2"
                v-for="zip in filteredZips"
                :key="zip.id"
        >
          {{ zip }}
          <span>x</span>
        </button>
        <error-field :errors="errors"
                     field="zips"
        />
      </div>
    </div>
    <div class="row my-3" v-if="$_can('companies.coverage.update')">
      <div class="col">
        <button type="button" class="btn btn-primary" @click="showImportModal = true">Import</button>
      </div>
    </div>
    <modal v-if="showImportModal" @close="closeModal" :size="'modal-large'">
      <template>
        <div slot="header">
          <div class="row">
            <div class="col-12">
              <div>
                <h4 class="mb-3">Import multiple ZIP codes:</h4>
              </div>
            </div>
          </div>
        </div>
        <div slot="body">
          <h5>Enter multiple zip codes</h5>
          <div class="row">
            <div class="col-12">
              <div class="select-wrap select-wrap-textarea">
                <label>Enter zip codes</label>
                <textarea rows="5" class="form-control" placeholder="" v-model="bulkZips" />
              </div>
              Please use commas to separate zip codes.
            </div>
          </div>
        </div>
        <div slot="footer">
          <div class="row">
            <div class="col-12 text-right">
              <button type="button" class="btn btn-primary ld-ext-right"
                      :class="loadingButton ? 'running' : ''"
                      :disabled="buttonDisabled"
                      @click="saveBulkZips"
              >
                Import
                <div class="ld ld-ball ld-flip" />
              </button>
            </div>
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>


<script>
import axios from 'axios';
import toastr from 'toastr';
import { mapActions, mapMutations, mapState } from 'vuex';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import * as turf from '@turf/turf';
import Modal from '../Modal';
import CompaniesStepsMenu from './CompaniesStepsMenu';
import ErrorField from '../ErrorField';
import Errors from '../../classes/Errors';
import CompaniesVisibilityBar from './CompaniesVisibilityBar';

export default {
  components: {
    CompaniesVisibilityBar,
    ErrorField,
    Modal,
    CompaniesStepsMenu,
  },
  props: {
    type: {
      type: String,
      default: 'edit',
    },
  },
  data: () => ({
    map: null,
    draw: {},
    zips: [],
    searchZips: '',
    showImportModal: false,
    bulkZips: '',
    token: process.env.MAPBOX_TOKEN,
    loading: true,
    loaderColor: '#2962ff',
    loadingButton: false,
    buttonDisabled: false,
    showTooltipPrefix: false,
    errors: new Errors(),
  }),
  computed: {
    ...mapState({
      company: state => state.companies.company,
    }),
    filteredZips() {
      return this.zips.filter(zip => zip.includes(this.searchZips));
    },
  },
  mounted() {
    this.loadZips();
    let lat = '34.052235';
    let lon = '-118.243683';
    if (this.company.longitude && this.company.latitude) {
      lat = this.company.latitude;
      lon = this.company.longitude;
    }

    if (document.getElementById('mapid')) {
      mapboxgl.accessToken = this.token;
      this.map = new mapboxgl.Map({
        container: 'mapid',
        style: 'mapbox://styles/mod24/cjivhg9y50v2i2qkztanglqc1',
        center: [lon, lat],
        minZoom: 2,
        zoom: 11,
      });

      this.draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
        },
      });
      this.map.addControl(this.draw);
      this.map.boxZoom.disable();

      this.map.on('load', () => {
        this.map.on('click', this.onMapClick);

        this.map.addSource('zip-zones', {
          type: 'geojson',
          data: '/static/zips_us_topo.geojson',
        });

        this.map.on('sourcedata', e => {
          if (e.isSourceLoaded && e.sourceId === 'zip-zones') {
            this.filterMap();
            setTimeout(() => {
              this.loading = false;
            }, 500);
          }
        });

        this.map.addLayer({
          id: 'zip-zones-highlight',
          type: 'fill',
          source: 'zip-zones',
          layout: {},
          paint: {
            'fill-color': '#627BC1',
            'fill-opacity': 0.2,
          },
        });

        this.map.addLayer({
          id: 'zip-zones-border',
          type: 'line',
          source: 'zip-zones',
          layout: {},
          paint: {
            'line-color': '#627BC1',
            'line-width': 1,
          },
        });

        this.map.addLayer({
          id: 'zip-zones-highlight-selected',
          type: 'fill',
          source: 'zip-zones',
          paint: {
            'fill-color': '#627BC1',
            'fill-opacity': 0.6,
          },
          filter: ['in', 'zip', ''],
        });

        this.map.on('draw.modechange', () => {
          if (this.draw.getMode() === 'draw_polygon') {
            this.map.off('click', this.onMapClick);
          }
        });

        this.map.on('draw.create', e => {
          const userPolygon = e.features[0];

          const polygonBoundingBox = turf.bbox(userPolygon);

          const southWest = [polygonBoundingBox[0], polygonBoundingBox[1]];
          const northEast = [polygonBoundingBox[2], polygonBoundingBox[3]];

          const northEastPointPixel = this.map.project(northEast);
          const southWestPointPixel = this.map.project(southWest);

          const features = this.map.queryRenderedFeatures(
            [southWestPointPixel, northEastPointPixel],
            { layers: ['zip-zones-highlight'] },
          );

          for (let i = 0; i < features.length; i += 1) {
            if (!this.zips.includes(features[i].properties.zip)) {
              this.zips.push(features[i].properties.zip);
            }
          }

          this.filterMap();
          this.draw.trash();
          setTimeout(() => {
            this.map.on('click', this.onMapClick);
          }, 50);
        });
      });
    }
  },
  methods: {
    ...mapActions(['getCompanies']),
    ...mapMutations(['setCompanyStep', 'setSavedCoverage']),
    addZip(zip) {
      if (zip !== undefined && this.zips.indexOf(zip) < 0) {
        this.zips.push(zip);
      } else if (zip !== undefined && this.zips.indexOf(zip) >= 0) {
        this.removeZip(zip);
      }
    },
    removeZip(val) {
      this.zips = this.zips.filter(zip => zip !== val);
      this.filterMap();
    },
    saveZips(zips) {
      this.buttonDisabled = true;
      this.loadingButton = true;
      axios
        .post(`/companies/${this.company.id}/zips`, { zips })
        .then(() => {
          if (this.type === 'edit') {
            toastr.success('Successfully saved coverage');
          } else {
            this.setCompanyStep('companies-settings');
            this.setSavedCoverage(true);
          }
          this.getCompanies();
          this.$emit('saved');
        })
        .catch(err => {
          this.errors.record(err.response.data.error);
        })
        .finally(() => {
          this.loadingButton = false;
          this.buttonDisabled = false;
        });
    },
    saveBulkZips() {
      this.buttonDisabled = true;
      this.loadingButton = true;
      const zips = this.bulkZips
        .trim()
        .replace(/(^,)|(,$)/g, '')
        .split(',');
      for (let i = 0; i < zips.length; i += 1) {
        const zip = zips[i].trim();
        if (zip.length) {
          this.addZip(zip);
        }
      }
      this.filterMap();
      this.closeModal();
      this.loadingButton = false;
      this.buttonDisabled = false;
    },
    filterMap() {
      this.$nextTick(() => {
        if (this.map) {
          this.map.setFilter('zip-zones-highlight-selected', ['in', 'zip', ...this.zips]);
        }
      });
    },
    loadZips() {
      const id = this.$route.params.id ? this.$route.params.id : this.company.id;
      axios.get(`/companies/${id}/zips`).then(res => {
        this.zips = res.data.data.map(zip => zip.zip);
        if (this.map && this.map.getLayer('zip-zones-highlight-selected') !== undefined) {
          this.filterMap();
        }
      });
    },
    closeModal() {
      this.showImportModal = false;
      this.bulkZips = '';
    },
    onMapClick(e) {
      const features = this.map.queryRenderedFeatures(e.point);
      if (features[0]) {
        this.addZip(features[0].properties.zip);
        this.filterMap();
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/companies/CompaniesMap.vue